import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Chip } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AppTable from "../../../shared/AppTable";
import AppButton from "../../../shared/AppButton";
import { API_BASE_URL } from "../../../config/apiConfig";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import Pagination from "../../../components/Pagination";

const API_URL = `${API_BASE_URL}/claim/tpa/report`;
const REPORTS_PER_PAGE = 10;

const authConfig = (responseType) => ({
  ...(responseType ? { responseType } : {}),
  headers: { Authorization: `Bearer ${localStorage.getItem("token")?.replaceAll('"', "")}` },
});

const dateOnly = (value) => (value ? new Date(value).toLocaleDateString() : "---");
const normalizeId = (value) => String(value || "").trim();

const parseBatchId = (value) => {
  const batchId = normalizeId(value);
  if (!batchId || batchId === "all") {
    return { tpaId: "", payerId: "" };
  }

  const combinedMatch = batchId.match(/^tpa-(.+)-payer-(.+)$/);
  if (combinedMatch) {
    return { tpaId: combinedMatch[1], payerId: combinedMatch[2] };
  }
  if (batchId.startsWith("tpa-")) {
    return { tpaId: batchId.slice(4), payerId: "" };
  }
  if (batchId.startsWith("payer-")) {
    return { tpaId: "", payerId: batchId.slice(6) };
  }

  return { tpaId: "", payerId: "" };
};

export default function TpaClaimsReportMain() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", tpa_id: "", payer_id: "" });

  const payerGroups = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("payersData") || "[]");
    } catch {
      return [];
    }
  }, []);

  const tpas = payerGroups;
  const allPayers = useMemo(
    () =>
      payerGroups.flatMap((group) =>
        (group.payers || []).map((payer) => ({
          ...payer,
          tpaId: normalizeId(group.id),
          tpaName: group.name_en,
        })),
      ),
    [payerGroups],
  );

  const selectedTpa = useMemo(
    () => tpas.find((item) => normalizeId(item.id) === normalizeId(filters.tpa_id)),
    [filters.tpa_id, tpas],
  );

  const payers = useMemo(() => {
    if (selectedTpa?.payers?.length) {
      return selectedTpa.payers.map((payer) => ({
        ...payer,
        tpaId: normalizeId(selectedTpa.id),
        tpaName: selectedTpa.name_en,
      }));
    }
    return allPayers;
  }, [allPayers, selectedTpa]);

  const getTpaNameById = useCallback(
    (id) => tpas.find((item) => normalizeId(item.id) === normalizeId(id))?.name_en,
    [tpas],
  );

  const getPayerById = useCallback(
    (id) => allPayers.find((item) => normalizeId(item.id) === normalizeId(id)),
    [allPayers],
  );

  const getReportTpaLabel = useCallback(
    (row) => {
      const { tpaId, payerId } = parseBatchId(row.batch_id);
      const payer = getPayerById(row.payer_id || payerId);
      const tpaName = getTpaNameById(tpaId) || payer?.tpaName;

      if (tpaName) return tpaName;
      if (!tpaId && !payerId) return t("All TPAs");
      return "---";
    },
    [getPayerById, getTpaNameById, t],
  );

  const getReportPayerLabel = useCallback(
    (row) => {
      const { payerId } = parseBatchId(row.batch_id);
      const payer = getPayerById(row.payer_id || payerId);

      if (payer?.name_en) return payer.name_en;
      if (!normalizeId(row.payer_id) && !payerId) return t("All Payers");
      return row.payer_id || payerId || "---";
    },
    [getPayerById, t],
  );

  const fetchReports = useCallback(async (page = currentPage) => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL, {
        ...authConfig(),
        params: { page, limit: REPORTS_PER_PAGE },
      });
      setReports(response.data?.data || []);
      setTotalPages(Number(response.data?.pages) || 1);
      setTotalReports(Number(response.data?.count) || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || t("Unable to load TPA reports"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, t]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const refreshStatus = async (id, silent = false) => {
    try {
      const response = await axios.get(`${API_URL}/status`, { ...authConfig(), params: { id } });
      const report = response.data?.data;
      if (report) setReports((current) => current.map((item) => (item.id === id ? report : item)));
      if (!silent && report?.status) toast.success(t(`Report status: ${report.status}`));
      return report;
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.message || t("Unable to refresh report status"));
      return null;
    }
  };

  useEffect(() => {
    const active = reports.filter((report) => ["queued", "processing"].includes(report.status));
    if (!active.length) return undefined;

    const timer = setInterval(() => {
      active.forEach((report) => refreshStatus(report.id, true));
    }, 4000);

    return () => clearInterval(timer);
  }, [reports]);

  const handleTpaChange = (event) => {
    const nextTpaId = event.target.value;
    setFilters((current) => {
      const nextTpa = tpas.find((item) => normalizeId(item.id) === normalizeId(nextTpaId));
      const currentPayerId = normalizeId(current.payer_id);
      const payerStillValid =
        !currentPayerId ||
        !nextTpa ||
        (nextTpa.payers || []).some((payer) => normalizeId(payer.id) === currentPayerId);

      return {
        ...current,
        tpa_id: nextTpaId,
        payer_id: payerStillValid ? current.payer_id : "",
      };
    });
  };

  const generateReport = async (event) => {
    event.preventDefault();

    if (!filters.from_date || !filters.to_date) {
      toast.error(t("From Date and To Date are required"));
      return;
    }
    if (filters.from_date > filters.to_date) {
      toast.error(t("From Date cannot be greater than To Date"));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        from_date: filters.from_date,
        to_date: filters.to_date,
        ...(normalizeId(filters.tpa_id) ? { tpa_id: filters.tpa_id } : {}),
        ...(normalizeId(filters.payer_id) ? { payer_id: filters.payer_id } : {}),
        format: "excel",
      };

      const response = await axios.post(API_URL, payload, authConfig());
      toast.success(response.data?.message || t("Report generation started"));
      if (currentPage === 1) {
        await fetchReports(1);
      } else {
        setCurrentPage(1);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || t("Unable to generate report"));
    } finally {
      setSubmitting(false);
    }
  };

  const download = async (report) => {
    try {
      const response = await axios.get(`${API_URL}/file`, { ...authConfig("blob"), params: { id: report.id } });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tpa-claims-report-${report.id}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.error || t("Report file is not ready"));
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t("Actions"),
        width: "88px",
        render: (row) => (
          <div className="flex items-center gap-2">
            <div
              title={t("Refresh Status")}
              role="button"
              tabIndex={0}
              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
              onClick={() => refreshStatus(row.id)}
              onKeyDown={(e) => e.key === "Enter" && refreshStatus(row.id)}
            >
              <RefreshIcon sx={{ fontSize: 19 }} />
            </div>
            <div
              title={t("Download")}
              role="button"
              tabIndex={row.status === "completed" ? 0 : -1}
              className={`flex items-center justify-center transition-colors ${
                row.status === "completed" ? "cursor-pointer text-gray-500 hover:text-blue-600" : "cursor-default text-gray-300"
              }`}
              onClick={() => row.status === "completed" && download(row)}
              onKeyDown={(e) => e.key === "Enter" && row.status === "completed" && download(row)}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 19 }} />
            </div>
          </div>
        ),
      },
      { header: t("Report ID"), render: (row) => row.id },
      { header: t("TPA"), render: (row) => getReportTpaLabel(row) },
      { header: t("Payer"), render: (row) => getReportPayerLabel(row) },
      { header: t("Start Date"), render: (row) => dateOnly(row.from_date) },
      { header: t("End Date"), render: (row) => dateOnly(row.to_date) },
      {
        header: t("Status"),
        render: (row) => (
          <Chip
            size="small"
            label={row.status || "---"}
            color={row.status === "completed" ? "success" : row.status === "failed" ? "error" : "info"}
          />
        ),
      },
      { header: t("Created At"), render: (row) => dateOnly(row.created_at) },
      {
        header: t("Error"),
        width: "240px",
        cellSx: { whiteSpace: "normal", overflowWrap: "anywhere" },
        render: (row) => <div className="max-w-[220px] whitespace-normal break-words">{row.error || "---"}</div>,
      },
    ],
    [getReportPayerLabel, getReportTpaLabel, t],
  );

  return (
    <div className="pre_auth_container !p-0 flex flex-col h-full">
      <div className="mb-2 border-b border-slate-200 pb-2 flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-slate-900">{t("TPA Claims Report")}</h1>
          <p className="mt-0.5 text-[12px] text-slate-500">{t("Generate TPA claims report")}</p>
        </div>
      </div>
      <form onSubmit={generateReport} className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
        <div className="flex flex-wrap items-end gap-1.5">
          <div className="w-full sm:w-[180px]">
            <CustomFilterInput
              label={t("TPA")}
              name="tpa_id"
              isSelect
              value={filters.tpa_id}
              onChange={handleTpaChange}
              placeholder={t("All TPAs")}
              renderOptions={() =>
                tpas.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name_en}
                  </option>
                ))
              }
            />
          </div>
          <div className="w-full sm:w-[150px]">
            <CustomFilterInput
              label={t("From Date")}
              name="from_date"
              type="date"
              required
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </div>
          <div className="w-full sm:w-[150px]">
            <CustomFilterInput
              label={t("To Date")}
              name="to_date"
              type="date"
              required
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </div>
          <div className="w-full sm:w-[260px]">
            <CustomFilterInput
              label={t("Payer")}
              name="payer_id"
              isSelect
              value={filters.payer_id}
              onChange={(e) => setFilters({ ...filters, payer_id: e.target.value })}
              placeholder={selectedTpa ? t("All Payers In TPA") : t("All Payers")}
              renderOptions={() =>
                payers.map((item) => (
                  <option key={`${item.tpaId || "any"}-${item.id}`} value={item.id}>
                    {selectedTpa ? item.name_en : `${item.name_en}${item.tpaName ? ` (${item.tpaName})` : ""}`}
                  </option>
                ))
              }
            />
          </div>
          <div className="flex gap-1.5">
            <AppButton type="submit" label={submitting ? t("Generating...") : t("Generate")} disabled={submitting} />
            <AppButton
              type="button"
              label={t("Refresh")}
              variant="outlined"
              startIcon={<RefreshIcon className="!text-[13px]" />}
              onClick={fetchReports}
            />
          </div>
        </div>
      </form>
      <div className="min-w-0 flex flex-col">
        <div className="min-h-[360px] overflow-hidden">
          <AppTable
            columns={columns}
            data={reports}
            loading={loading}
            actions={false}
            containerClassName="custom-table-scrollbar"
          />
        </div>
        {totalPages > 1 && (
          <div className="mt-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
            <div className="text-right text-xs text-gray-500">
              {t("Total")}: {totalReports}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
